import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { NgControl } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
/**
 * PercentageFormatterDirective
 *
 * This directive formats an input field as a percentage.
 * It works only with Angular Reactive Forms (FormControl/FormGroup),
 * It handles focus, input restriction, and blur events.
 *
 * Usage:
 * <input type="text" formControlName="CommisionPercent" appPercentageFormatter [maxPercentage]="100">
 *
 * Features:
 * - On focus: shows raw numeric value
 * - On input: restricts input to numbers and up to 4 decimals
 * - On blur: formats the value as a percentage string with 2–4 decimals
 */
@Directive({
    selector: '[appPercentageFormatter]',
    standalone: true,
    providers: [DecimalPipe]
})

export class PercentageFormatterDirective {
    @Input() maxPercentage: number = 100;

    constructor(
        private el: ElementRef<HTMLInputElement>,
        private control: NgControl,
        private decimalPipe: DecimalPipe
    ) { }

    /**
     * On focus:
     * - Shows the raw numeric value (removes the '%' sign)
     * - If the current form control value is 0, the input will appear empty
     *   to let the user type a new number easily
     *
     * Examples:
     * 1. Form control value = 12.34 → input shows "12.34"
     * 2. Form control value = 0     → input shows "" (empty string)
     * 3. Form control value = null  → input shows "" (empty string)
     */
    @HostListener('focus', ['$event'])
    onFocus(event: FocusEvent) {
        const value = this.control.value || 0;
        const input = this.el.nativeElement;
        input.value = value === 0 ? '' : value.toString();
    }

    /**
     * On input:
     * - Restricts input to numeric characters and decimal point
     * - Allows only one decimal point and up to 4 decimal places
     * - Caps the value to `maxPercentage`
     * 
     * Examples:
     * - Typing "123.45678" → will be trimmed to "123.4567"
     * - Typing "105" with maxPercentage=100 → will be capped to "100"
     */
    @HostListener('input', ['$event'])
    onInput(event: Event) {
        const input = this.el.nativeElement;
        let value = input.value.replace(/%/g, '').replace(/[^0-9.]/g, '');

        const parts = value.split('.');
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }

        if (parts[1]?.length > 4) {
            parts[1] = parts[1].slice(0, 4);
            value = parts.join('.');
        }

        if (value.startsWith('00')) {
            value = value.replace(/^0+/, '0');
            if (value.startsWith('.')) {
                value = '0' + value;
            }
        } else if (/^0[0-9]/.test(value)) {
            value = value.replace(/^0+/, '');
        }

        let num = parseFloat(value);
        if (!isNaN(num) && num > this.maxPercentage) {
            num = this.maxPercentage;
            value = num.toFixed(parts[1] ? 4 : 0);
        }

        if (value === this.maxPercentage.toString()) {
            value = this.maxPercentage.toString();
        } else if (value.startsWith(this.maxPercentage.toString() + '.')) {
            value = this.maxPercentage.toString();
        }

        input.value = value;
        this.control.control?.setValue(value, { emitEvent: true });
    }

    /**
     * On blur:
     * - Formats the numeric value as a percentage string with 2–4 decimals
     * - Updates the underlying form control value as a number
     * 
     * Examples:
     * - Input "12.3" → formats to "12.30%"
     * - Input "0" → formats to "0.00%"
     * - Input "123.4567" → if maxPercentage=100, formats to "100.00%"
     */
    @HostListener('blur', ['$event'])
    onBlur(event: FocusEvent) {
        const input = this.el.nativeElement;
        let rawValue = input.value.replace(/[^0-9.]/g, '');
        let numValue = parseFloat(rawValue) || 0;

        if (numValue > this.maxPercentage) numValue = this.maxPercentage;

        this.control.control?.setValue(numValue);

        const formatted =
            numValue === 0
                ? '0.00%'
                : this.decimalPipe.transform(numValue, '1.2-4') + '%';

        input.value = formatted;
    }
}
